import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Grid,
    Step,
    StepLabel,
    Stepper,
    Tooltip,
    Box
} from "@mui/material/";
import actions from "../../Store/actions";
import { strings } from "../../common/i18n";
import { useNavigate } from "react-router-dom";
import {
    ReturnAuditDetails,
    Auditors,
    Branches,
    Locations,
    SelectDates
} from "./components";
import styles from "./styles";
import makeStyles from "@mui/styles/makeStyles";
import SummaryView from "./components/AuditSummary";
import LeaveAuditDialog from "../../Audits/Create/components/LeaveAuditDialogue";
import { logToSentry } from "../../config/Sentry";

/**
 * Audits Create Component
 */

// order matters
const auditSteps = [
    {
        label: strings("dealerships"),
        component: <Branches />
    },
    { label: strings("locations"), component: <Locations /> },
    { label: strings("dates"), component: <SelectDates /> },
    {
        label: strings("auditors"),
        component: (
            <>
                {/* <ReturnAuditors /> */}
                <Auditors />
            </>
        )
    },
    { label: strings("details"), component: <ReturnAuditDetails /> }
];

export default () => {
    const { auditsCreate, branchLocations } = useSelector((state) => state);
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();
    const [activeStep, setActiveStep] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            try {
                dispatch(
                    actions.auditsCreate.create.itemSet({
                        leavingAudit: false,
                        pendingNavigation: null
                    })
                );
            } catch (error) {
                logToSentry("On navigating from auditsCreate:", { error });
            }
        };
    }, [dispatch]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = ""; // triggers the native prompt
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    function getStepContent(activeStep) {
        return auditSteps[activeStep].component;
    }
    const selectedAuditors = useMemo(() => {
        const res = auditsCreate.auditors.filter(
            (item) => item.selectedAuditors === "false"
        );

        return res.length > 0 ? true : false;
    }, [auditsCreate.auditors]);

    //  on selecting auditors
    useEffect(() => {}, [auditsCreate.auditors]);

    useEffect(() => {
        // Step 1 data is built by the branchLocations effect below.

        // Load audit groups
        (async () => {
            await dispatch(actions.auditGroups.create.getRequest());
        })();

        // on component unmount reset store
        return () => {
            dispatch(actions.auditsCreate.create.clearReducer());
        };
    }, []);

    // init() builds step 1 from the branch locations already in the store. On a
    // direct load of this route (reload, bookmark, new tab) they are usually
    // still in flight — Auth's `await dispatch(...)` resolves when the action is
    // queued, not when the request returns — so the first init found nothing
    // and step 1 spun forever. Build it again once they arrive.
    useEffect(() => {
        if (auditsCreate.data.length === 0 && branchLocations.data.length > 0)
            dispatch(actions.auditsCreate.create.init());
    }, [branchLocations.data]);

    return (
        <>
            <Grid container className={classes.fullHeight}>
                <Grid item xs={12} className={classes.stepperControl}>
                    <Button
                        color="primary"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        className={classes.controlBtn}
                        sx={{ mr: 1 }}
                    >
                        {strings("back")}
                    </Button>
                    <Stepper
                        activeStep={activeStep}
                        className={classes.stepper}
                    >
                        {auditSteps.map((obj, index) => {
                            return (
                                <Step key={obj.label}>
                                    <StepLabel>{obj.label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                    {activeStep !== auditSteps.length - 1 && (
                        <Tooltip
                            title={
                                activeStep === 0 &&
                                auditsCreate.selectedBranches.length === 0
                                    ? strings("messages.selectBranch")
                                    : activeStep === 1 &&
                                      auditsCreate.selectedLocations.ids
                                          .length === 0
                                    ? strings("messages.selectLocation")
                                    : activeStep === 3 && selectedAuditors
                                    ? strings("messages.selectAuditor")
                                    : ""
                            }
                            disableHoverListener={
                                !(
                                    (activeStep === 0 &&
                                        (auditsCreate.selectedBranches
                                            ?.length ?? 0) === 0) ||
                                    (activeStep === 1 &&
                                        (auditsCreate.selectedLocations?.ids
                                            ?.length ?? 0) === 0) ||
                                    (activeStep === 3 && selectedAuditors)
                                )
                            }
                        >
                            <Box sx={{ display: "inline-block" }}>
                                <Button
                                    onClick={handleNext}
                                    disabled={
                                        (activeStep === 0 &&
                                            auditsCreate.selectedBranches
                                                .length === 0) ||
                                        (activeStep === 1 &&
                                            auditsCreate.selectedLocations.ids
                                                .length === 0) ||
                                        (activeStep === 3 && selectedAuditors)
                                    }
                                    className={`${classes.controlBtn} ${
                                        (activeStep === 0 &&
                                            auditsCreate.selectedBranches
                                                .length === 0) ||
                                        (activeStep === 1 &&
                                            auditsCreate.selectedLocations.ids
                                                .length === 0) ||
                                        (activeStep === 3 && selectedAuditors)
                                            ? classes.highlightDisabled
                                            : ""
                                    }`}
                                >
                                    {strings("next")}
                                </Button>
                            </Box>
                        </Tooltip>
                    )}
                </Grid>
                <Grid container item xs={12} className={classes.dataList}>
                    <Grid item xs={9}>
                        {getStepContent(activeStep)}
                    </Grid>
                    <Grid item xs={3}>
                        <SummaryView />
                    </Grid>
                </Grid>
            </Grid>

            <LeaveAuditDialog
                isModalShown={auditsCreate.leavingAudit}
                onClose={() => {
                    dispatch(
                        actions.auditsCreate.create.itemSet({
                            pendingNavigation: null,
                            leavingAudit: false
                        })
                    );
                }}
                onConfirm={() => {
                    const pendingNavigation = auditsCreate.pendingNavigation;
                    if (pendingNavigation) {
                        dispatch(
                            actions.auditsCreate.create.itemSet({
                                pendingNavigation: null,
                                leavingAudit: false
                            })
                        );
                        navigate(pendingNavigation);
                    }
                }}
            />
        </>
    );
};

//TODO: To write a single line function to highlight disabled for all the pages(use a useMemo  and check based on the condition)
